import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { catchError, EMPTY, filter, from, map, mergeMap, of, withLatestFrom } from 'rxjs';

import * as UserActions from '../../../core/users/user.actions';
import * as UserSelectors from '../../../core/users/user.selectors';
import { User } from '../../user-administration/models';
import { NoteService } from './note.service';
import * as NoteActions from './note.actions';
import * as NoteSelectors from './note.selectors';
import { Note } from './models';
import { ProductService } from '../products/products.service';

function enrichNote(note: Note, users: User[]): Note {
  return {
    ...note,
    createdByName: users.find(user => user.id === note.createdBy)?.displayName ?? note.createdBy,
    updatedByName: note.updatedBy
      ? users.find(user => user.id === note.updatedBy)?.displayName
      : null,
    recipientNames: note.recipients.map(
      recipient => users.find(user => user.id === recipient.azureId)?.displayName ?? recipient.name
    ),
  };
}

function enrichNotes(notes: Note[], users: User[]): Note[] {
  return notes.map(note => enrichNote(note, users));
}

export const loadNotes$: ReturnType<typeof createEffect> = createEffect(
  (actions$ = inject(Actions), noteService = inject(NoteService)) => {
    return actions$.pipe(
      ofType(NoteActions.loadNotes),
      mergeMap(
        ({
          pageSize,
          page,
          sort,
          creatorAzureIds,
          recipientAzureIds,
          includeArchived,
          categoryIds,
        }) =>
          noteService
            .getAll(
              pageSize,
              page,
              sort,
              creatorAzureIds,
              includeArchived,
              recipientAzureIds,
              categoryIds
            )
            .pipe(
              map(response =>
                NoteActions.loadNotesSuccess({
                  notes: response.data,
                  totalCount: response.totalCount,
                })
              ),
              catchError(error =>
                of(
                  NoteActions.loadNotesFailure({
                    error: error.message ?? 'Unknown error loading notes',
                  })
                )
              )
            )
      )
    );
  },
  { functional: true }
);

export const loadNote$: ReturnType<typeof createEffect> = createEffect(
  (actions$ = inject(Actions), noteService = inject(NoteService)) => {
    return actions$.pipe(
      ofType(NoteActions.loadNote),
      mergeMap(({ id }) =>
        noteService.getById(id).pipe(
          map((note: Note) => NoteActions.loadNoteSuccess({ note })),
          catchError(error =>
            of(
              NoteActions.loadNoteFailure({
                error: error.message ?? `Unknown error loading note: ${id}`,
              })
            )
          )
        )
      )
    );
  },
  { functional: true }
);

export const deleteNote$: ReturnType<typeof createEffect> = createEffect(
  (actions$ = inject(Actions), noteService = inject(NoteService)) => {
    return actions$.pipe(
      ofType(NoteActions.deleteNote),
      mergeMap(({ id }) =>
        noteService.delete(id).pipe(
          map(() => NoteActions.deleteNoteSuccess({ id })),
          catchError(error =>
            of(
              NoteActions.deleteNoteFailure({
                error: error.message ?? `Unknown error deleting note: ${id}`,
              })
            )
          )
        )
      )
    );
  },
  { functional: true }
);

export const enrichNoteWithProductName$: ReturnType<typeof createEffect> = createEffect(
  (actions$ = inject(Actions), productService = inject(ProductService)) => {
    return actions$.pipe(
      ofType(NoteActions.loadNoteSuccess),
      filter(({ note }) => !!note.productId),
      mergeMap(({ note }) =>
        productService.getById(note.productId!).pipe(
          map(product => NoteActions.enrichNoteWithProductName({ productName: product.name })),
          catchError(() => EMPTY)
        )
      )
    );
  },
  { functional: true }
);

export const enrichSelectedNoteWithUsers$: ReturnType<typeof createEffect> = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) => {
    return actions$.pipe(
      ofType(NoteActions.loadNoteSuccess),
      withLatestFrom(
        store.select(UserSelectors.selectUsersLoaded),
        store.select(UserSelectors.selectAllUsers)
      ),
      map(([{ note }, usersLoaded, users]) => {
        if (usersLoaded) {
          return NoteActions.enrichSelectedNoteWithUserNames({ note: enrichNote(note, users) });
        }
        return UserActions.loadUsers();
      })
    );
  },
  { functional: true }
);

export const enrichNotesWithUsers$: ReturnType<typeof createEffect> = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) => {
    return actions$.pipe(
      ofType(NoteActions.loadNotesSuccess),
      withLatestFrom(
        store.select(UserSelectors.selectUsersLoaded),
        store.select(UserSelectors.selectAllUsers)
      ),
      map(([{ notes }, usersLoaded, users]) => {
        if (usersLoaded) {
          return NoteActions.enrichNotesWithUserNames({ notes: enrichNotes(notes, users) });
        }
        return UserActions.loadUsers();
      })
    );
  },
  { functional: true }
);

export const enrichNotesAfterUsersLoad$: ReturnType<typeof createEffect> = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) => {
    return actions$.pipe(
      ofType(UserActions.loadUsersSuccess),
      withLatestFrom(
        store.select(NoteSelectors.selectAllNotes),
        store.select(NoteSelectors.selectSelectedNote)
      ),
      mergeMap(([{ users }, notes, selectedNote]) => {
        const actionsToDispatch: (
          | ({
              note: Note;
            } & Action<string>)
          | ({
              notes: Note[];
            } & Action<string>)
        )[] = [
          NoteActions.enrichNotesWithUserNames({ notes: enrichNotes(notes, users) }),
          ...(selectedNote
            ? [
                NoteActions.enrichSelectedNoteWithUserNames({
                  note: enrichNote(selectedNote, users),
                }),
              ]
            : []),
        ];
        return from(actionsToDispatch);
      })
    );
  },
  { functional: true }
);

export const noteEffects: object = {
  loadNotes$,
  loadNote$,
  deleteNote$,
  enrichNoteWithProductName$,
  enrichSelectedNoteWithUsers$,
  enrichNotesWithUsers$,
  enrichNotesAfterUsersLoad$,
};
